import { useEffect, useState } from "react";
import { produce } from "immer";
import useToggleArray from "../../../../../hooks/useToggleArray";
import { dateRangeOperators } from "../../DataExplorerUI/ExplorerInputs/FieldOperatorList";
import { isInteger, omit } from "lodash-es";
import convertFiltersToOperatorsMode from "../../../../../Editors/BlockEdit/VisualizationGuiEditSection/convertFiltersToOperatorsMode";
import usePrevious from "../../../../../utils/usePrevious";
import isEqual from "react-fast-compare";
import { getGroupTypeOrAndRegularFilters } from "../../../../../store/actions/queryBuilder/queryBuilder";

const applyConversion = (filters, doConversion) =>
  doConversion ? convertFiltersToOperatorsMode(filters) : filters;

// move OR blocks always to bottom, regular filters always on top
export function getOrderedFilters(filters) {
  const { groupTypeOrFilters, regularFilters } =
    getGroupTypeOrAndRegularFilters(filters);

  return [...regularFilters, ...groupTypeOrFilters];
}

export function useFilters(
  initialFilters = [],
  doConversion = false,
  externalUpdate = false,
  rerenderComponent
) {
  const initialFiltersState = getOrderedFilters(
    applyConversion(initialFilters, doConversion)
  );

  const [draftFilters, setDraftFilters] = useState(initialFiltersState);
  const [muted, setMuted] = useToggleArray();
  const previousFilters = usePrevious(initialFilters);

  useEffect(() => {
    if (rerenderComponent) {
      setDraftFilters(initialFiltersState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerenderComponent]);

  useEffect(() => {
    if (!isEqual(initialFilters, previousFilters) && !externalUpdate) {
      setDraftFilters(
        getOrderedFilters(applyConversion(initialFilters, doConversion))
      );
    }
  }, [doConversion, externalUpdate, initialFilters, previousFilters]);

  const hasIgnoreCaseSensitivity = draftFilters.some(
    (filter) =>
      filter.ignoreCaseSensitivity ||
      (filter.filters ?? []).some((f) => f.ignoreCaseSensitivity)
  );

  const addFilter = () => {
    return setDraftFilters((filters) =>
      getOrderedFilters([
        ...filters,
        {
          name: null,
          values: [],
          ...(filters.length === 0 && { ignoreCaseSensitivity: "1" }),
        },
      ])
    );
  };

  const setField = (position, field) => {
    if (Array.isArray(draftFilters) && draftFilters.length) {
      validatePosition(position);
    }
    const nextState = produce(draftFilters, (draftState) => {
      draftState[position] = {
        name: field,
        operator: null,
        values: [],
        ...(draftFilters[position]?.ignoreCaseSensitivity && {
          ignoreCaseSensitivity: "1",
        }),
      };
    });
    return setDraftFilters(nextState);
  };

  const setConditional = (position, conditional) => {
    validatePosition(position);
    const nextState = produce(draftFilters, (draftState) => {
      draftState[position].conditional = conditional;
    });
    return setDraftFilters(nextState);
  };

  const setInnerFilterConditional = (index, innerIndex, conditional) => {
    validatePosition(index);
    const nextState = produce(draftFilters, (draftState) => {
      draftState[index].filters[innerIndex].conditional = conditional;
    });
    return setDraftFilters(nextState);
  };

  function updateSubQueryItem(filter, key, value) {
    // Check if the filter object has a subQueryValues property
    if (filter.subQueryValues) {
      // If it does, update the value of the specified key in subQueryValues
      filter.subQueryValues[key] = value;
    } else {
      // Otherwise, update the value of the specified key in the filter object directly
      filter[key] = value;
    }
  }

  // for numeric operators we need to clear null or empty string values
  function getIsNumericOperator(operator) {
    return [">", "<", "<=", ">="].includes(operator);
  }

  // for numeric operators we need to clear null or empty string values
  function clearNumericValues(values) {
    return (values ?? []).filter((val) => val !== null && val !== "");
  }

  const setOperator = (position, operator) => {
    validatePosition(position);
    const nextState = produce(draftFilters, (draftState) => {
      const filter = draftState[position];
      const isNumeric = getIsNumericOperator(operator);

      // change operator of sub query if exist
      updateSubQueryItem(filter, "operator", operator);

      if (dateRangeOperators.includes(operator)) {
        filter.values = [null, null];
      }

      if (isNumeric) {
        filter.values = clearNumericValues(filter.values);
      }
    });
    return setDraftFilters(nextState);
  };

  const setValues = (position, values) => {
    validatePosition(position);
    const nextState = produce(draftFilters, (draftState) => {
      const filter = draftState[position];
      // change value of sub query if exist
      updateSubQueryItem(filter, "values", values);
    });
    return setDraftFilters(nextState);
  };

  const removeRow = (position) => {
    validatePosition(position);
    return setDraftFilters(draftFilters.filter((s, i) => i !== position));
  };

  function isMuted(index) {
    return muted.find((m) => m === index) !== undefined;
  }

  function validatePosition(position) {
    if (!isInteger(position) || !draftFilters[position]) {
      throw new Error("Parameter 1 must be the filter index");
    }
  }

  const invalidFilters = draftFilters.filter((f) => {
    if (f.filters) {
      return f.filters.every((f) => {
        if (f.subQueryValues) {
          return !f.subQueryValues.name;
        }
        return !f.name || !f.operator || !f.values?.length;
      });
    }

    return (
      !f.name || !f.operator || !f.values?.length || notZeroNumber(f.values[0])
    );
  });

  function setupSubQuery(filter, type) {
    const { subQueryValues, filters } = filter;

    // create subQuery aggregation
    if (!subQueryValues) {
      return {
        name: "",
        subQueryValues: {
          ...filter,
          aggregation: { type },
        },
        ...(filters && { filters }),
      };
    }

    // remove subQuery aggregation
    const currentType = subQueryValues.aggregation?.type;
    if (currentType === type) {
      return omit(filter, "subQueryValues");
    }

    // change subQuery aggregation type
    return {
      ...filter,
      subQueryValues: {
        ...subQueryValues,
        aggregation: { type },
      },
    };
  }

  // type is aggregation type
  function setAggregating(index, type) {
    const filters = draftFilters.map((filter, i) => {
      if (i === index) {
        return setupSubQuery(filter, type);
      }
      return filter;
    });
    return setDraftFilters(filters);
  }

  function setInnerFilterAggregating(index, innerIndex, type) {
    const updatedFilters = draftFilters.map((filter, i) => {
      if (i !== index) return filter;

      const updatedInnerFilters = filter.filters.map((innerFilter, j) => {
        return j === innerIndex
          ? setupSubQuery(innerFilter, type)
          : innerFilter;
      });

      return { ...filter, filters: updatedInnerFilters };
    });

    setDraftFilters(updatedFilters);
  }

  function setAggregatedGroupBy(index, field) {
    const filters = draftFilters.map((filter, i) =>
      i === index ? { ...filter, name: field } : filter
    );
    return setDraftFilters(filters);
  }

  function setInnerFilterAggregatedGroupBy(index, innerIndex, field) {
    const updatedFilters = draftFilters.map((filter, i) => {
      if (i !== index) return filter;

      const updatedInnerFilters = filter.filters.map((innerFilter, j) => {
        return j === innerIndex ? { ...innerFilter, name: field } : innerFilter;
      });

      return { ...filter, filters: updatedInnerFilters };
    });

    setDraftFilters(updatedFilters);
  }

  const defaultProps = {
    name: "",
    operator: null,
    values: [],
  };

  function addBlock() {
    const filters = [
      ...draftFilters,
      {
        filters: [
          {
            ...defaultProps,
            ...(hasIgnoreCaseSensitivity && { ignoreCaseSensitivity: "1" }),
          },
        ],
      },
    ];

    setDraftFilters(filters);
  }

  function removeBlock(index) {
    const filters = draftFilters.filter((_, i) => i !== index);

    setDraftFilters(filters);
  }

  function addInnerFilter(index) {
    const filters = draftFilters.map((filter, i) =>
      i === index
        ? {
            ...filter,
            filters: [
              ...filter.filters,
              {
                ...defaultProps,
                ...(hasIgnoreCaseSensitivity && { ignoreCaseSensitivity: "1" }),
              },
            ],
          }
        : filter
    );
    setDraftFilters(filters);
  }

  // this handle field name, operator and values for OR filters
  function setInnerFilterByKey(index, innerIndex, value, key) {
    const isNumeric = getIsNumericOperator(value);

    const filters = draftFilters.map((filter, i) => {
      if (i === index) {
        return {
          ...filter,
          filters: filter.filters.map((innerFilter, j) =>
            j === innerIndex
              ? {
                  ...innerFilter,
                  [key]: value,
                  ...(isNumeric && {
                    values: clearNumericValues(innerFilter.values),
                  }),
                }
              : innerFilter
          ),
        };
      }

      return filter;
    });

    setDraftFilters(filters);
  }

  // remove filter from OR filters
  function removeInnerFilterRow(index, innerIndex) {
    const filters = draftFilters
      .map((filter, i) => {
        if (i === index) {
          const filters = filter.filters.filter((_, j) => j !== innerIndex);

          // if there are no any filter available then remove filters at all
          return filters.length
            ? {
                ...filter,
                filters,
              }
            : omit(filter, ["filters", "isMuted"]);
        }

        return filter;
      })
      // remove empty objects
      .filter((value) => Object.keys(value).length);

    setDraftFilters(filters);
  }

  function removeInnerFilterRowValue(index, innerIndex, valueIndex) {
    const filters = draftFilters.map((filter, i) => {
      if (i === index) {
        return {
          ...filter,
          filters: filter.filters.map((innerFilter, j) =>
            j === innerIndex
              ? {
                  ...innerFilter,
                  values: innerFilter.values.filter((_, k) => k !== valueIndex),
                }
              : innerFilter
          ),
        };
      }

      return filter;
    });
    setDraftFilters(filters);
  }

  function setIgnoreCaseSensitivity() {
    const filters = draftFilters.map((filter) =>
      hasIgnoreCaseSensitivity
        ? omit(
            {
              ...filter,
              ...(filter.filters && {
                filters: filter.filters.map((f) =>
                  omit(f, "ignoreCaseSensitivity")
                ),
              }),
            },
            "ignoreCaseSensitivity"
          )
        : {
            ...filter,
            ignoreCaseSensitivity: "1",
            ...(filter.filters && {
              filters: filter.filters.map((f) => ({
                ...f,
                ignoreCaseSensitivity: "1",
              })),
            }),
          }
    );
    setDraftFilters(filters);
  }

  return {
    draftFilters,
    setDraftFilters,
    addFilter,
    setField,
    setOperator,
    setValues,
    removeRow,
    muted,
    setMuted,
    isMuted,
    invalidFilters,
    // apiFilters
    setAggregating,
    setAggregatedGroupBy,
    addBlock,
    setInnerFilterByKey,
    removeInnerFilterRow,
    removeInnerFilterRowValue,
    setInnerFilterAggregating,
    setInnerFilterAggregatedGroupBy,
    addInnerFilter,
    removeBlock,
    quantity: draftFilters?.length ?? 0,
    ignoreCaseSensitivity: hasIgnoreCaseSensitivity,
    setIgnoreCaseSensitivity,
    setConditional,
    setInnerFilterConditional,
  };
}

function notZeroNumber(val) {
  return !val && val !== 0;
}
